import { getAvailableCommands } from './commandRegistry.js';
import { getCommandDefinition } from './commandRegistry.js';
import { openhaspEventPayload } from '../Hasp/controlTypes.js';
import { getEventDefinition } from '../Hasp/properties/v2/wireFormat.js';

export function getEventVariables(sourceNode, event) {
    const definition = sourceNode ? getEventDefinition(sourceNode, event) : null;
    return openhaspEventPayload(sourceNode, event, definition?.payload ?? [], definition);
}

// Navigation has its own per-event rule; audio commands repeat with another
// resource, file or volume, on any event.
const REPEATABLE_CATEGORIES = new Set(['Navigation', 'Audio']);

/** Whether a command may be used only once per source object. */
export function isUniqueCommand(command) {
    return Boolean(command) && !REPEATABLE_CATEGORIES.has(command.category);
}

export function collectUsedCommandIds(handlers, exceptActionId) {
    return new Set((handlers ?? []).flatMap(handler => handler.actions ?? [])
        .filter(action => action.type === 'command' && action.id !== exceptActionId && action.commandId)
        .map(action => action.commandId));
}

export function getCommandsForContext({ sourceNode, event, handlers, currentActionId }) {
    const variables = new Set(getEventVariables(sourceNode, event));
    const used = collectUsedCommandIds(handlers, currentActionId);
    const currentAction = (handlers ?? []).flatMap(handler => handler.actions ?? [])
        .find(action => action.id === currentActionId);
    const currentCommandId = currentAction?.commandId;
    const navigationUsedInEvent = (handlers ?? [])
        .filter(handler => handler.event === event)
        .flatMap(handler => handler.actions ?? [])
        .some(action => action.id !== currentActionId &&
            getCommandDefinition(action.commandId)?.category === 'Navigation');
    return getAvailableCommands().filter(command => {
        if (command.id === currentCommandId) return true;
        if (command.category === 'Navigation' && navigationUsedInEvent) return false;
        if (command.sourceTypes && !command.sourceTypes.includes(sourceNode?.obj)) return false;
        if (command.events && !command.events.includes(event)) return false;
        if ((command.requiresVariables ?? []).some(variable => !variables.has(variable))) return false;
        return !isUniqueCommand(command) || !used.has(command.id);
    });
}

export function getCommandContextError(action, sourceNode, event) {
    const command = getCommandDefinition(action?.commandId);
    if (!command) return action?.commandId ? 'Command is not defined by the current schema' : '';
    if (command.sourceTypes && !command.sourceTypes.includes(sourceNode?.obj)) {
        return 'Command is not supported by this object';
    }
    if (command.events && !command.events.includes(event)) return 'Command is not supported for this event';
    const variables = new Set(getEventVariables(sourceNode, event));
    if ((command.requiresVariables ?? []).some(variable => !variables.has(variable))) {
        return 'Command requires a value provided by another event';
    }
    return '';
}

export function hasDuplicateNavigationCommand(action, handler, handlers) {
    if (getCommandDefinition(action?.commandId)?.category !== 'Navigation') return false;
    return (handlers ?? [])
        .filter(candidate => candidate.event === handler?.event)
        .flatMap(candidate => candidate.actions ?? [])
        .some(candidate => candidate.id !== action.id &&
            getCommandDefinition(candidate.commandId)?.category === 'Navigation');
}
